import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  InitialRequestSchema,
  PlatformError,
  PlatformState,
} from './slice.types';
import { SidebarState } from '../../views/components/Sidebar/index.types';
import {
  OrganizationDataOutSchema,
  OrganizationSchema,
} from 'src/utils/api.interfaces';
import { LocalStorageKeys } from 'src/common/constants';
import { OrganizationTag } from 'src/utils/tags';

// The organization last worked in is remembered across reloads, but only for
// the user who chose it: everybody who logs in on this browser shares its
// storage, and a Raben root admin can open every carrier, so another user's
// leftover id would otherwise be accepted as their own.
const rememberOrganization = (
  organizationId: number | null,
  userId: string | number | undefined
) => {
  if (!organizationId) {
    localStorage.removeItem(LocalStorageKeys.ACTIVE_ORGANIZATION_ID);
    localStorage.removeItem(LocalStorageKeys.ACTIVE_ORGANIZATION_USER_ID);
    return;
  }
  localStorage.setItem(
    LocalStorageKeys.ACTIVE_ORGANIZATION_ID,
    String(organizationId)
  );
  // No user (the token-based survey pages) means no owner, so no login
  // will ever adopt this value.
  if (userId === undefined)
    localStorage.removeItem(LocalStorageKeys.ACTIVE_ORGANIZATION_USER_ID);
  else
    localStorage.setItem(
      LocalStorageKeys.ACTIVE_ORGANIZATION_USER_ID,
      String(userId)
    );
};

// Where a freshly identified user starts. The remembered organization stays
// only if this very user chose it and still has it; otherwise a member of the
// Raben root starts on the root, and anyone else on no organization (the
// product home then makes the usual choice).
const chooseStartingOrganizationId = (
  userId: string | number,
  organizations: OrganizationSchema[],
  rememberedId: number | null
): number | null => {
  const rememberedByUser =
    localStorage.getItem(LocalStorageKeys.ACTIVE_ORGANIZATION_USER_ID) ===
    String(userId);
  if (
    rememberedId !== null &&
    rememberedByUser &&
    organizations.some((org) => org.id === rememberedId)
  )
    return rememberedId;
  return (
    organizations.find((org) => org.tags.includes(OrganizationTag.RabenRoot))
      ?.id ?? null
  );
};

const getInitialState = (): PlatformState => ({
  isLoading: false,
  isAuthenticated: undefined,
  error: null,
  sidebarState: {
    isControllable: true,
    isOpen: true,
    isShown: true, // default is set by current Layout
  },
  user: null,
  organizations: null,
  availableProducts: [],
  allowAccessToManageOrgStructure: false,
  // Read before we know who is logged in, so a reload keeps the open page;
  // setInitialRequest drops it once users/me shows it is not this user's.
  activeOrganizationId:
    parseInt(
      localStorage.getItem(LocalStorageKeys.ACTIVE_ORGANIZATION_ID) || '',
      10
    ) || null,
  activeOrganization: null,
  platformSubscriber: { name: '', maxNumOrganizations: 0 },
  tags: [],
});

const platformSlice = createSlice({
  name: 'platform',
  initialState: getInitialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      return { ...state, isLoading: action.payload };
    },
    setError: (state, action: PayloadAction<PlatformError>) => {
      return { ...state, error: action.payload };
    },
    setInitialRequest: (state, action: PayloadAction<InitialRequestSchema>) => {
      // users/me is refetched during a session; only the first answer for a
      // user may decide where they start, later ones must not undo their choice.
      const isNewUser =
        state.user === null ||
        String(state.user.id) !== String(action.payload.id);

      state.user = {
        id: action.payload.id,
        email: action.payload.email,
        isSuperUser: action.payload.is_super_user,
        ecoinventProLicenceIsValid:
          action.payload.is_ecoinvent_pro_licence_valid,
        isPasswordSet: action.payload.is_password_set,
      };
      state.organizations = action.payload.organizations;
      state.availableProducts = action.payload.available_products;
      state.allowAccessToManageOrgStructure =
        action.payload.allow_access_to_manage_org_structure;

      state.platformSubscriber = {
        name: action.payload.platform_subscriber.name,
        maxNumOrganizations:
          action.payload.platform_subscriber.max_num_organizations,
      };
      state.tags = action.payload.tags;

      if (isNewUser) {
        state.activeOrganizationId = chooseStartingOrganizationId(
          action.payload.id,
          action.payload.organizations ?? [],
          state.activeOrganizationId
        );
        rememberOrganization(state.activeOrganizationId, action.payload.id);
      }
    },
    setIsAuthenticated(state, action: PayloadAction<boolean>) {
      state.isAuthenticated = action.payload;
    },
    setActiveOrganizationId: (state, action: PayloadAction<number | null>) => {
      // TODO: this could just be persisted via redux
      rememberOrganization(action.payload, state.user?.id);
      state.activeOrganizationId = action.payload;
    },
    setActiveOrganization: (
      state,
      action: PayloadAction<OrganizationDataOutSchema | null>
    ) => {
      state.activeOrganization = action.payload;
    },
    updateSidebarState: (
      state,
      action: PayloadAction<Partial<SidebarState>>
    ) => {
      Object.assign(state.sidebarState, action.payload);
    },
  },
});

export const {
  setLoading,
  setError,
  setInitialRequest,
  setIsAuthenticated,
  setActiveOrganizationId,
  setActiveOrganization,
  updateSidebarState,
} = platformSlice.actions;

export default platformSlice.reducer;
